import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';

interface ExpertiseData {
  overall_score: number;
  rank_title: string;
  badge_color: string;
  valuation_vnd: number;
  valuation_usd: number;
  dimensions: {
    domain_depth: number;
    operational_sop: number;
    habit_streak: number;
    cognitive_agility: number;
    community_impact: number;
  };
  metrics: {
    total_documents: number;
    total_chunks: number;
    growth_streak_days: number;
    flashcards_mastered: number;
    operations_tabs_active: number;
    community_likes: number;
  };
  recommendations: Array<{
    id: string;
    text: string;
    priority: 'high' | 'medium' | 'low';
  }>;
  calculated_at?: string;
}

export function ExpertiseScorePage() {
  const navigate = useNavigate();
  const [data, setData] = useState<ExpertiseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);

  useEffect(() => {
    loadScore();
  }, []);

  const loadScore = async () => {
    setLoading(true);
    try {
      const res = await api.getExpertiseScore();
      setData(res);
    } catch (e) {
      console.error('Failed to load expertise score', e);
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      const res = await api.recalculateExpertiseScore();
      if (res.result) {
        setData(res.result);
      } else {
        await loadScore();
      }
    } catch (e: any) {
      alert('Lỗi tái thẩm định: ' + e.message);
    } finally {
      setRecalculating(false);
    }
  };

  if (loading || !data) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: '16px' }}>
        <div className="spinner" style={{ width: 32, height: 32 }} />
        <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Đang thẩm định năng lực & tính toán chỉ số tri thức...</span>
      </div>
    );
  }

  // Radar chart mathematical coordinates (Pentagon 5 axes)
  const dims = [
    { label: 'Độ sâu chuyên môn', key: 'domain_depth', value: data.dimensions.domain_depth, weight: '25%' },
    { label: 'Quy trình SOP', key: 'operational_sop', value: data.dimensions.operational_sop, weight: '25%' },
    { label: 'Kỷ luật phản tư', key: 'habit_streak', value: data.dimensions.habit_streak, weight: '20%' },
    { label: 'Độ nhạy bén', key: 'cognitive_agility', value: data.dimensions.cognitive_agility, weight: '15%' },
    { label: 'Uy tín cộng đồng', key: 'community_impact', value: data.dimensions.community_impact, weight: '15%' },
  ];

  const radarRadius = 100;
  const centerX = 140;
  const centerY = 135;

  const getCoordinates = (index: number, valPercent: number) => {
    const angle = (Math.PI * 2 / 5) * index - Math.PI / 2;
    const r = (valPercent / 100) * radarRadius;
    const x = centerX + r * Math.cos(angle);
    const y = centerY + r * Math.sin(angle);
    return { x, y, angle };
  };

  const polygonPoints = dims
    .map((d, i) => {
      const { x, y } = getCoordinates(i, d.value);
      return `${x},${y}`;
    })
    .join(' ');

  const gridCircles = [20, 40, 60, 80, 100];

  return (
    <div style={{ padding: '28px 32px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Top Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h1 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)', margin: 0 }}>
              🏆 Thẩm Định Năng Lực & Định Giá Vault
            </h1>
            <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '12px', background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', border: '1px solid rgba(56, 189, 248, 0.3)' }}>
              Erix Cognitive Index v1.4
            </span>
          </div>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '6px', marginBottom: 0 }}>
            Lượng hóa tài sản vô hình và chất xám tích lũy trong Kho báu trí tuệ nhân tạo của bạn thành chỉ số và giá trị vốn hóa.
          </p>
        </div>

        <button
          onClick={handleRecalculate}
          disabled={recalculating}
          className="btn btn--primary"
          style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 18px' }}
        >
          {recalculating ? (
            <>
              <div className="spinner" style={{ width: 14, height: 14 }} />
              <span>Đang tính toán lại...</span>
            </>
          ) : (
            <>
              <span>⚡ Tái Thẩm Định Chỉ Số</span>
            </>
          )}
        </button>
      </div>

      {/* Top Hero Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px', marginBottom: '28px' }}>
        {/* Card 1: Score & Rank */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(124, 58, 237, 0.08) 100%)',
          border: '1px solid rgba(124, 58, 237, 0.25)',
          borderRadius: '16px',
          padding: '24px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.8px', color: 'var(--text-muted)', fontWeight: 600 }}>
              Điểm Năng Lực Trí Tuệ (Cognitive Score)
            </span>
            <span style={{
              fontSize: '11px',
              padding: '2px 10px',
              borderRadius: '20px',
              background: 'rgba(255, 255, 255, 0.08)',
              color: data.badge_color,
              fontWeight: 600,
              border: `1px solid ${data.badge_color}40`
            }}>
              Thang điểm 1000
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', marginTop: '16px' }}>
            <span style={{ fontSize: '54px', fontWeight: '800', color: '#fff', lineHeight: 1, letterSpacing: '-1px' }}>
              {data.overall_score}
            </span>
            <span style={{ fontSize: '18px', color: 'var(--text-muted)' }}>/ 1000</span>
          </div>

          {/* Progress bar */}
          <div style={{ width: '100%', height: '8px', background: 'rgba(255, 255, 255, 0.08)', borderRadius: '4px', overflow: 'hidden', marginTop: '16px' }}>
            <div style={{
              width: `${(data.overall_score / 1000) * 100}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #38bdf8, #818cf8, #c084fc)',
              borderRadius: '4px',
              transition: 'width 0.6s ease'
            }} />
          </div>

          <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '18px' }}>🎖️</span>
            <div>
              <div style={{ fontSize: '14px', fontWeight: '700', color: data.badge_color }}>
                {data.rank_title}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                Xếp hạng dựa trên dữ liệu thực tế và tần suất tương tác
              </div>
            </div>
          </div>
        </div>

        {/* Card 2: Vault Valuation */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(16, 185, 129, 0.08) 100%)',
          border: '1px solid rgba(16, 185, 129, 0.25)',
          borderRadius: '16px',
          padding: '24px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.8px', color: 'var(--text-muted)', fontWeight: 600 }}>
              Định Giá Tài Sản Tri Thức (Vault Capital)
            </span>
            <span style={{
              fontSize: '11px',
              padding: '2px 10px',
              borderRadius: '20px',
              background: 'rgba(16, 185, 129, 0.15)',
              color: '#34d399',
              fontWeight: 600,
              border: '1px solid rgba(16, 185, 129, 0.3)'
            }}>
              Có thể chuyển nhượng
            </span>
          </div>

          <div style={{ marginTop: '16px' }}>
            <div style={{ fontSize: '38px', fontWeight: '800', color: '#34d399', lineHeight: 1.1 }}>
              {data.valuation_vnd.toLocaleString('vi-VN')} ₫
            </div>
            <div style={{ fontSize: '15px', color: 'var(--text-muted)', marginTop: '4px', fontWeight: '500' }}>
              ≈ ${data.valuation_usd.toLocaleString('en-US')} USD
            </div>
          </div>

          <div style={{
            marginTop: '20px',
            padding: '10px 14px',
            background: 'rgba(255, 255, 255, 0.04)',
            borderRadius: '8px',
            fontSize: '12px',
            color: 'var(--text-muted)',
            lineHeight: 1.5
          }}>
            💡 <strong>Cơ sở định giá:</strong> Mỗi 100 điểm năng lực tương đương 3.6 triệu VND vốn hóa trí tuệ nhân bản, dựa trên thời gian đào tạo mô hình RAG và mật độ SOP sẵn sàng thực thi.
          </div>
        </div>

        {/* Card 3: Metrics summary */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid var(--border)',
          borderRadius: '16px',
          padding: '24px'
        }}>
          <div style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.8px', color: 'var(--text-muted)', fontWeight: 600, marginBottom: '16px' }}>
            Chỉ Số Nền Tảng Trong Kho Báu
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <div style={{ padding: '10px', background: 'rgba(255, 255, 255, 0.03)', borderRadius: '10px' }}>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>📂 Tài liệu & Chunks</div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--text-primary)', marginTop: '3px' }}>
                {data.metrics.total_documents} docs <span style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 'normal' }}>({data.metrics.total_chunks} chunks)</span>
              </div>
            </div>

            <div style={{ padding: '10px', background: 'rgba(255, 255, 255, 0.03)', borderRadius: '10px' }}>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>🔥 Chuỗi phản tư</div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#f59e0b', marginTop: '3px' }}>
                {data.metrics.growth_streak_days} ngày liên tục
              </div>
            </div>

            <div style={{ padding: '10px', background: 'rgba(255, 255, 255, 0.03)', borderRadius: '10px' }}>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>🧠 Thẻ thuộc lòng</div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#818cf8', marginTop: '3px' }}>
                {data.metrics.flashcards_mastered} thẻ thuần thục
              </div>
            </div>

            <div style={{ padding: '10px', background: 'rgba(255, 255, 255, 0.03)', borderRadius: '10px' }}>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>🤝 Tương tác cộng đồng</div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#f43f5e', marginTop: '3px' }}>
                {data.metrics.community_likes} lượt yêu thích ❤️
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Middle Section: 5-Dimension Radar & Breakdown */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 340px) 1fr', gap: '24px', marginBottom: '28px' }}>
        {/* Radar Chart Visualizer */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid var(--border)',
          borderRadius: '16px',
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{ width: '100%', fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '12px' }}>
            🕸️ Radar 5 Chiều Năng Lực
          </div>

          <svg width="280" height="270" viewBox="0 0 280 270" style={{ overflow: 'visible' }}>
            {/* Background Grid Concentric Polygons */}
            {gridCircles.map(level => {
              const pts = dims
                .map((_, i) => {
                  const { x, y } = getCoordinates(i, level);
                  return `${x},${y}`;
                })
                .join(' ');
              return (
                <polygon
                  key={level}
                  points={pts}
                  fill="none"
                  stroke="rgba(255, 255, 255, 0.07)"
                  strokeWidth="1"
                />
              );
            })}

            {/* Axes lines */}
            {dims.map((_, i) => {
              const { x, y } = getCoordinates(i, 100);
              return (
                <line
                  key={i}
                  x1={centerX}
                  y1={centerY}
                  x2={x}
                  y2={y}
                  stroke="rgba(255, 255, 255, 0.12)"
                  strokeWidth="1"
                  strokeDasharray="2,2"
                />
              );
            })}

            {/* Data Area Polygon */}
            <polygon
              points={polygonPoints}
              fill="rgba(56, 189, 248, 0.25)"
              stroke="#38bdf8"
              strokeWidth="2.5"
            />

            {/* Points & Labels */}
            {dims.map((d, i) => {
              const { x, y, angle } = getCoordinates(i, d.value);
              const labelRadius = radarRadius + 22;
              const lx = centerX + labelRadius * Math.cos(angle);
              const ly = centerY + labelRadius * Math.sin(angle);

              return (
                <g key={d.key}>
                  <circle cx={x} cy={y} r="4" fill="#38bdf8" stroke="#fff" strokeWidth="1.5" />
                  <text
                    x={lx}
                    y={ly}
                    fill="#94a3b8"
                    fontSize="10"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontWeight="500"
                  >
                    {d.value}%
                  </text>
                </g>
              );
            })}
          </svg>

          <div style={{ fontSize: '11px', color: 'var(--text-muted)', textAlign: 'center', marginTop: '10px' }}>
            Mô hình đánh giá đa trọng số theo chuẩn Erix Competency Matrix
          </div>
        </div>

        {/* Dimension Breakdown Cards */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid var(--border)',
          borderRadius: '16px',
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '4px' }}>
            Chi Tiết Từng Trụ Cột Năng Lực
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {dims.map((dim) => (
              <div key={dim.key} style={{
                padding: '12px 16px',
                borderRadius: '12px',
                background: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid rgba(255, 255, 255, 0.05)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>{dim.label}</span>
                    <span style={{ fontSize: '10px', color: 'var(--text-muted)', background: 'rgba(255, 255, 255, 0.05)', padding: '1px 6px', borderRadius: '4px' }}>
                      Trọng số {dim.weight}
                    </span>
                  </div>
                  <span style={{ fontSize: '14px', fontWeight: '700', color: dim.value >= 70 ? '#34d399' : dim.value >= 40 ? '#38bdf8' : '#fbbf24' }}>
                    {dim.value} / 100
                  </span>
                </div>
                <div style={{ width: '100%', height: '6px', background: 'rgba(255, 255, 255, 0.06)', borderRadius: '3px', overflow: 'hidden' }}>
                  <div style={{
                    width: `${dim.value}%`,
                    height: '100%',
                    background: dim.value >= 70 ? 'linear-gradient(90deg, #10b981, #34d399)' : dim.value >= 40 ? 'linear-gradient(90deg, #0284c7, #38bdf8)' : 'linear-gradient(90deg, #d97706, #fbbf24)',
                    borderRadius: '3px'
                  }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Section: Actionable Growth Recommendations & Rank Ladder */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px' }}>
        {/* Recommendations */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid var(--border)',
          borderRadius: '16px',
          padding: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <span style={{ fontSize: '18px' }}>💡</span>
            <span style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)' }}>
              Lộ Trình Tăng Trưởng Điểm Năng Lực Tiếp Theo
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {data.recommendations.map(rec => (
              <div key={rec.id} style={{
                padding: '14px 16px',
                borderRadius: '12px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.06)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    marginTop: '2px',
                    background: rec.priority === 'high' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                    color: rec.priority === 'high' ? '#f87171' : '#fbbf24'
                  }}>
                    {rec.priority}
                  </span>
                  <span style={{ fontSize: '13px', color: 'var(--text-primary)', lineHeight: 1.4 }}>
                    {rec.text}
                  </span>
                </div>

                <button
                  className="btn btn--secondary btn--sm"
                  style={{ whiteSpace: 'nowrap', fontSize: '12px' }}
                  onClick={() => {
                    if (rec.text.includes('tài liệu')) navigate('/documents');
                    else if (rec.text.includes('Spaced Repetition')) navigate('/flashcards');
                    else if (rec.text.includes('Collective Feed')) navigate('/collective');
                    else navigate('/operations');
                  }}
                >
                  Thực Hiện ➔
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Rank Ladder */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid var(--border)',
          borderRadius: '16px',
          padding: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <span style={{ fontSize: '18px' }}>🧗</span>
            <span style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)' }}>
              Thang Bậc Danh Hiệu Chuyên Môn
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[
              { score: '850 - 1000', title: 'Cố Vấn Lĩnh Vực Cấp Cao (Domain Authority)', icon: '👑', color: '#a855f7', min: 850 },
              { score: '700 - 849', title: 'Chiến Lược Gia Tri Thức (Senior Strategist)', icon: '🎖️', color: '#38bdf8', min: 700 },
              { score: '500 - 699', title: 'Chuyên Gia Độc Lập (Specialist Consultant)', icon: '💎', color: '#34d399', min: 500 },
              { score: '350 - 499', title: 'Chuyên Viên Thực Chiến (Practitioner)', icon: '⚡', color: '#fbbf24', min: 350 },
              { score: '0 - 349', title: 'Học Viên Khởi Tạo (Knowledge Apprentice)', icon: '🌱', color: '#94a3b8', min: 0 }
            ].map(tier => {
              const isCurrent = data.overall_score >= tier.min && (tier.min === 850 || data.overall_score < (tier.min === 700 ? 850 : tier.min === 500 ? 700 : tier.min === 350 ? 500 : 350));
              return (
                <div key={tier.title} style={{
                  padding: '10px 14px',
                  borderRadius: '10px',
                  background: isCurrent ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.02)',
                  border: isCurrent ? `1.5px solid ${tier.color}` : '1px solid rgba(255, 255, 255, 0.05)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '16px' }}>{tier.icon}</span>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: isCurrent ? 700 : 500, color: isCurrent ? tier.color : 'var(--text-primary)' }}>
                        {tier.title}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                        {tier.score} điểm
                      </div>
                    </div>
                  </div>
                  {isCurrent && (
                    <span style={{
                      fontSize: '11px',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      background: tier.color,
                      color: '#000',
                      fontWeight: 700
                    }}>
                      HIỆN TẠI
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
